import type { StaffAttentionSource, StaffAttentionSummary } from "@lafayette/schemas";

import { countText, sourceCopy } from "../home/labels";
import { actionableCount } from "../home/queries";
import { type AreaKey, isAreaOpenable, type Offering } from "../navigation/offering";

// What the shell shows from the attention summary (`GET /staff/attention`, the same read as Home): two counts for the sidebar and the
// entries of the bell. The shell adds no request and no rule of its own; it counts the sources the API already answers.

export type SidebarBadge = Readonly<{
  /** `4`, or `50+` when the source stopped at its cap. */
  text: string;
  /** The words a screen reader adds after the name of the entry. */
  spoken: string;
  /** Where the entry leads while it wears this count: the list the count came from, so the number and what the person sees agree. */
  to?: string;
}>;

type BadgedArea = Extract<AreaKey, "concierge" | "requests">;

/** A new area wears a quiet "New" badge until the team knows it (CHT-2027, Analytics). It needs no read: it is the same for every person. */
export const NEW_AREA_BADGES: Readonly<Partial<Record<AreaKey, SidebarBadge>>> = {
  analytics: { text: "New", spoken: "area" },
};

/**
 * The source behind each badge. Concierge counts the conversations that wait for a person (a customer wrote last, or a customer has open
 * work): `conversations_need_staff`. Requests counts the open requests that are the staff member's own: `requests_assigned_to_me`.
 * Both are "for your information" sources, so they never add to the bell.
 */
const BADGE_SOURCES: Readonly<
  Record<
    BadgedArea,
    Readonly<{ key: StaffAttentionSource["key"]; spoken: (count: number) => string }>
  >
> = {
  concierge: {
    key: "conversations_need_staff",
    spoken: (count) =>
      count === 1 ? "conversation waits for a person" : "conversations wait for a person",
  },
  requests: {
    key: "requests_assigned_to_me",
    spoken: (count) => (count === 1 ? "open request is yours" : "open requests are yours"),
  },
};

/**
 * A badge for each area that has a count to show. A zero count, a source the API did not send (the role may not use it) and a source that
 * could not be read all show no badge: a number that is not known is never drawn as a number.
 */
export function sidebarBadges(
  summary: StaffAttentionSummary | undefined,
): Readonly<Partial<Record<BadgedArea, SidebarBadge>>> {
  if (summary === undefined) return {};
  const badges: Partial<Record<BadgedArea, SidebarBadge>> = {};
  for (const [area, { key, spoken }] of Object.entries(BADGE_SOURCES) as Array<
    [BadgedArea, (typeof BADGE_SOURCES)[BadgedArea]]
  >) {
    const source = summary.sources.find((candidate) => candidate.key === key);
    if (source === undefined || source.status === "unavailable" || source.count === 0) continue;
    badges[area] = {
      text: countText(source),
      spoken: spoken(source.count),
      to: sourceCopy[key].link,
    };
  }
  return badges;
}

export type BellEntry = Readonly<{
  key: StaffAttentionSource["key"];
  /** `2 handoffs at risk`. */
  text: string;
  /** Where the list is. */
  to: string;
  urgent: boolean;
}>;

export type BellState = Readonly<{
  /**
   * The dot: at least one source that needs action has something in it, or a count is only a lower bound (a queue could not be read, or
   * stopped at its cap), so a blind queue never looks empty. Acting on the work, not opening the bell, makes it go.
   */
  dot: boolean;
  /** The sources that need action and are not empty, urgent first, only those whose area opens for this staff member. */
  entries: readonly BellEntry[];
  /** Some source could not be read, so the list may be short. */
  blind: boolean;
  /** The summary could not be read at all. */
  unreadable: boolean;
}>;

/**
 * What needs the staff member now, for the bell: the sources of severity `urgent` or `attention` (never `info`) with a count above zero.
 * "New" means "not done yet": the dot stays while the work stays. The same sources as the Home page lists under "Needs action now".
 */
export function bellState(
  summary: StaffAttentionSummary | undefined,
  offering: Offering,
  failed = false,
): BellState {
  if (summary === undefined) return { dot: false, entries: [], blind: false, unreadable: failed };
  const actionable = summary.sources.filter((source) => source.severity !== "info");
  const counted = actionableCount(summary);
  const entries = actionable
    .filter((source) => source.status === "ok" && source.count > 0)
    .filter((source) => isAreaOpenable(sourceCopy[source.key].area, offering))
    .sort((a, b) => Number(b.severity === "urgent") - Number(a.severity === "urgent"))
    .map(
      (source): BellEntry => ({
        key: source.key,
        text: `${countText(source)} ${sourceCopy[source.key].label(source.count)}`,
        to: sourceCopy[source.key].link,
        urgent: source.severity === "urgent",
      }),
    );
  return {
    dot: counted.count > 0 || counted.partial,
    entries,
    blind: actionable.some((source) => source.status === "unavailable"),
    unreadable: false,
  };
}
